import { useEffect, useMemo, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Building2, ExternalLink, Trash2, Pause, Play, Edit3, Download, Search, Filter } from "lucide-react";
import { toast } from "sonner";

const emptyCreate = {
  slug: "", name: "", brand_color: "#0F8964", accent_color: "#0284C7",
  admin_name: "", contact_email: "", contact_phone: "",
};

export default function SuperAdminClinics() {
  const [tenants, setTenants] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [minPatients, setMinPatients] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  const [confirmDel, setConfirmDel] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status !== "all") params.set("status", status);
    const { data } = await api.get(`/super-admin/tenants?${params.toString()}`);
    setTenants(data);
  };
  useEffect(() => { load(); }, [q, status]);

  const filtered = useMemo(() => {
    const min = minPatients === "" ? -1 : Number(minPatients);
    return tenants.filter((t) => {
      if (min >= 0 && t.patient_count < min) return false;
      if (createdFrom && new Date(t.created_at) < new Date(createdFrom)) return false;
      if (createdTo && new Date(t.created_at) > new Date(createdTo + "T23:59:59")) return false;
      return true;
    });
  }, [tenants, minPatients, createdFrom, createdTo]);

  const create = async () => {
    try {
      await api.post("/super-admin/tenants", createForm);
      toast.success("Clinic onboarded");
      setCreateOpen(false);
      setCreateForm(emptyCreate);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const openEdit = (t) => {
    setEditing(t);
    setEditForm({
      name: t.name, brand_color: t.brand_color, accent_color: t.accent_color,
      admin_name: t.admin_name || "", contact_email: t.contact_email || "", contact_phone: t.contact_phone || "",
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/super-admin/tenants/${editing.id}`, editForm);
      toast.success("Updated");
      setEditing(null);
      await load();
    } catch { toast.error("Update failed"); }
  };

  const toggleSuspend = async (t) => {
    try {
      const { data } = await api.post(`/super-admin/tenants/${t.id}/suspend`);
      toast.success(data.active ? "Re-activated" : "Suspended");
      await load();
    } catch { toast.error("Action failed"); }
  };

  const removeHard = async () => {
    if (confirmText !== confirmDel.slug) {
      toast.error("Type the slug to confirm");
      return;
    }
    try {
      await api.delete(`/super-admin/tenants/${confirmDel.id}?hard=true`);
      toast.success("Clinic permanently removed");
      setConfirmDel(null); setConfirmText("");
      await load();
    } catch { toast.error("Could not remove"); }
  };

  const exportCsv = async () => {
    try {
      const resp = await api.get("/super-admin/tenants/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "medrewards_clinics.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const resetFilters = () => { setMinPatients(""); setCreatedFrom(""); setCreatedTo(""); setStatus("all"); setQ(""); };

  return (
    <AdminShell title="Clinics & hospitals" scope="super">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-mr-secondary" />
          <Input data-testid="clinic-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or slug…" className="pl-9 w-80 rounded-full bg-white border-mr h-10" />
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="clinic-export-btn" variant="outline" className="rounded-full" onClick={exportCsv}>
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="clinic-add-btn" className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]"><Plus className="h-4 w-4 mr-1" /> Onboard clinic</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">Onboard clinic</DialogTitle></DialogHeader>
              <div className="space-y-3 max-h-[60vh] overflow-auto">
                <div><Label>Slug (subdomain)</Label><Input data-testid="clinic-create-slug" value={createForm.slug} onChange={(e) => setCreateForm({ ...createForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} placeholder="stmary" /></div>
                <div><Label>Clinic name</Label><Input data-testid="clinic-create-name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} /></div>
                <div><Label>Primary admin name</Label><Input data-testid="clinic-create-admin" value={createForm.admin_name} onChange={(e) => setCreateForm({ ...createForm, admin_name: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Contact email</Label><Input data-testid="clinic-create-email" type="email" value={createForm.contact_email} onChange={(e) => setCreateForm({ ...createForm, contact_email: e.target.value })} /></div>
                  <div><Label>Contact phone</Label><Input data-testid="clinic-create-phone" value={createForm.contact_phone} onChange={(e) => setCreateForm({ ...createForm, contact_phone: e.target.value })} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Brand color</Label><Input data-testid="clinic-create-color" type="color" value={createForm.brand_color} onChange={(e) => setCreateForm({ ...createForm, brand_color: e.target.value })} /></div>
                  <div><Label>Accent color</Label><Input data-testid="clinic-create-accent" type="color" value={createForm.accent_color} onChange={(e) => setCreateForm({ ...createForm, accent_color: e.target.value })} /></div>
                </div>
              </div>
              <DialogFooter><Button data-testid="clinic-create-save" onClick={create} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-white border border-mr rounded-2xl p-4 mb-4">
        <div className="flex items-center gap-2 mb-3 text-mr-primary">
          <Filter className="h-4 w-4" /><p className="font-display font-semibold">Filters</p>
          <button data-testid="clinic-filters-reset" onClick={resetFilters} className="ml-auto text-xs text-mr-accent">Reset</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="clinic-filter-status" className="mt-1 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Min patients</Label>
            <Input data-testid="clinic-filter-minp" type="number" value={minPatients} onChange={(e) => setMinPatients(e.target.value)} className="mt-1 h-9" />
          </div>
          <div>
            <Label className="text-xs">Created from</Label>
            <Input data-testid="clinic-filter-from" type="date" value={createdFrom} onChange={(e) => setCreatedFrom(e.target.value)} className="mt-1 h-9" />
          </div>
          <div>
            <Label className="text-xs">Created to</Label>
            <Input data-testid="clinic-filter-to" type="date" value={createdTo} onChange={(e) => setCreatedTo(e.target.value)} className="mt-1 h-9" />
          </div>
        </div>
      </div>

      <p className="label-eyebrow text-mr-secondary mb-3">{filtered.length} of {tenants.length} clinics</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((t) => (
          <div key={t.id} data-testid={`tenant-${t.id}`} className={`bg-white border rounded-2xl p-6 transition-all ${t.active ? "border-mr" : "border-mr opacity-70"}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="h-10 w-10 rounded-xl bg-[#E2E8F0] flex items-center justify-center"><Building2 className="h-5 w-5 text-mr-accent" /></div>
              <div className="flex items-center gap-2">
                {t.active ? <Badge className="rounded-full bg-mr-success text-white">Active</Badge> : <Badge className="rounded-full bg-mr-secondary text-white">Suspended</Badge>}
                <span className="h-5 w-5 rounded-full" style={{ background: t.brand_color }} />
              </div>
            </div>
            <h3 className="font-display text-lg font-semibold text-mr-primary">{t.name}</h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span data-testid={`tenant-type-${t.id}`} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase ${t.org_type === "hospital" ? "bg-mr-accent/10 text-mr-accent" : "bg-mr-primary/10 text-mr-primary"}`}>
                {t.org_type === "hospital" ? "Hospital" : "Clinic"}
              </span>
              <p className="text-mr-secondary text-sm">/{t.slug} · {t.patient_count} patient{t.patient_count === 1 ? "" : "s"}</p>
            </div>
            {t.admin_name && <p className="text-xs text-mr-secondary mt-2">Admin: {t.admin_name}{t.contact_email ? ` · ${t.contact_email}` : ""}</p>}

            <div className="mt-4 flex items-center justify-between border-t border-mr-border pt-3">
              <a href={`/c/${t.slug}`} target="_blank" rel="noreferrer" className="text-mr-accent text-sm inline-flex items-center gap-1">Visit <ExternalLink className="h-3 w-3" /></a>
              <div className="flex items-center gap-1">
                <Button data-testid={`tenant-edit-${t.id}`} variant="ghost" size="icon" onClick={() => openEdit(t)} title="Edit"><Edit3 className="h-4 w-4 text-mr-primary" /></Button>
                <Button
                  data-testid={`tenant-suspend-${t.id}`}
                  variant="ghost" size="icon"
                  onClick={() => toggleSuspend(t)}
                  title={t.active ? "Suspend" : "Re-activate"}
                >
                  {t.active ? <Pause className="h-4 w-4 text-mr-warning" /> : <Play className="h-4 w-4 text-mr-success" />}
                </Button>
                <Button data-testid={`tenant-del-${t.id}`} variant="ghost" size="icon" onClick={() => { setConfirmDel(t); setConfirmText(""); }} title="Remove"><Trash2 className="h-4 w-4 text-mr-accent" /></Button>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-center text-mr-secondary col-span-full py-10">No clinics match your filters.</p>}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Edit {editing?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-auto">
            <div><Label>Name</Label><Input data-testid="edit-name" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div><Label>Admin name</Label><Input data-testid="edit-admin" value={editForm.admin_name || ""} onChange={(e) => setEditForm({ ...editForm, admin_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact email</Label><Input data-testid="edit-email" type="email" value={editForm.contact_email || ""} onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })} /></div>
              <div><Label>Contact phone</Label><Input data-testid="edit-phone" value={editForm.contact_phone || ""} onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Brand color</Label><Input data-testid="edit-color" type="color" value={editForm.brand_color || "#0F8964"} onChange={(e) => setEditForm({ ...editForm, brand_color: e.target.value })} /></div>
              <div><Label>Accent color</Label><Input data-testid="edit-accent" type="color" value={editForm.accent_color || "#0284C7"} onChange={(e) => setEditForm({ ...editForm, accent_color: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="edit-save" onClick={saveEdit} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && (setConfirmDel(null), setConfirmText(""))}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-mr-accent">Permanently remove clinic?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-mr-secondary text-sm">
              This will <b>permanently delete</b> "{confirmDel?.name}" and ALL associated patients, transactions, rewards, tiers, campaigns and notifications. This action cannot be undone.
            </p>
            <p className="text-mr-secondary text-sm">
              If you just want to disable the clinic temporarily, use <b>Suspend</b> instead.
            </p>
            <div>
              <Label>Type <code className="font-mono bg-mr-bg px-2 py-0.5 rounded">{confirmDel?.slug}</code> to confirm</Label>
              <Input data-testid="confirm-del-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="mt-2" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setConfirmDel(null); setConfirmText(""); }}>Cancel</Button>
            <Button data-testid="confirm-del-btn" disabled={confirmText !== confirmDel?.slug} onClick={removeHard} className="rounded-full bg-mr-accent hover:bg-[var(--mr-accent-hover)]">
              <Trash2 className="h-4 w-4 mr-1" /> Permanently remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
